import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Star, Heart, Lock, ArrowLeft, Coins, Edit3 } from "lucide-react";
import Trophy from "../components/Trophy";
import SetPriceButton, { BuyContentButton } from "../components/ContentPrice";
import AgeGateGuard from "../components/AgeGateGuard";

export default function NovelleView() {
  return (
    <AgeGateGuard>
      <NovelleViewInner />
    </AgeGateGuard>
  );
}

function NovelleViewInner() {
  const { novelleId } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [novelle, setNovelle] = useState(null);
  const [trophy, setTrophy] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [tokenBalance, setTokenBalance] = useState(0);

  const load = () =>
    api.get(`/noveller/${novelleId}`).then((r) => setNovelle(r.data));

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [novelleId]);

  useEffect(() => {
    if (!novelleId) return;
    api.post("/contests/content-trophies", { type: "novelle", ids: [novelleId] })
      .then((r) => setTrophy(r.data?.results?.[novelleId] || null))
      .catch(() => setTrophy(null));
  }, [novelleId]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/tokens/me").then((r) => setTokenBalance(r.data.balance || 0)).catch(() => {});
    }
  }, [user]);

  const unlock = async () => {
    if (!user || user === false) {
      navigate("/login");
      return;
    }
    setUnlocking(true);
    setError("");
    try {
      const r = await api.post(`/noveller/${novelleId}/unlock`);
      if (r.data.balance !== undefined) setTokenBalance(r.data.balance);
      await load();
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUnlocking(false);
    }
  };

  const toggleLike = async () => {
    if (!user || user === false) return;
    try {
      await api.post(`/noveller/${novelleId}/like`);
      await load();
    } catch {}
  };

  const rate = async (n) => {
    if (!user || user === false) return;
    try {
      await api.post(`/noveller/${novelleId}/rate`, { rating: n });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (!novelle)
    return (
      <Layout>
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      </Layout>
    );

  const canRead = novelle.has_unlocked || novelle.is_author || novelle.token_cost === 0;
  const isGuest = !user || user === false;

  return (
    <Layout withSidebar={false}>
      <div className="font-meta mb-6">
        <Link to="/noveller" className="hover:text-[var(--cream)] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Bibliotek
        </Link>
      </div>

      <div className="max-w-3xl mx-auto">
        <header className="border-b border-white/10 pb-8 mb-8">
          {!isGuest && (
            <div className="flex flex-wrap gap-2 mb-4">
              {novelle.themes?.map((t) => (
                <span key={t} className="font-meta text-[10px] border border-white/10 px-2 py-1">
                  {t}
                </span>
              ))}
            </div>
          )}

          <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight mb-4">
            {novelle.title}
          </h1>

          {trophy && !isGuest && (
            <div className="mb-4">
              <Trophy
                place={trophy.place}
                contestTitle={trophy.contest_title}
                contestId={trophy.contest_id}
                variant="badge"
                size={16}
              />
            </div>
          )}

          {!isGuest && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-meta">
              <Link to={`/profile/${novelle.author?.username}`} className="hover:text-[var(--magenta)]">
                {novelle.author?.username || "ukjent"}
              </Link>
              <span>·</span>
              <span>{novelle.word_count} ord</span>
              <span>·</span>
              <span>{formatRelative(novelle.created_at)}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                {novelle.read_count} lest
              </span>
            </div>
          )}

          {novelle.content_warnings?.length > 0 && !isGuest && (
            <div className="mt-4 font-meta text-[var(--magenta)]">
              ⚠ {novelle.content_warnings.join(" · ")}
            </div>
          )}

          {novelle.is_author && (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Link
                to={`/noveller/${novelle.id}/rediger`}
                className="btn-ghost inline-flex items-center text-xs"
              >
                <Edit3 className="h-3 w-3 mr-2" /> Rediger
              </Link>
              <SetPriceButton
                contentType="novelle"
                contentId={novelle.id}
                currentPrice={novelle.token_cost || 0}
                onSaved={() => load()}
                variant="default"
              />
            </div>
          )}

          {!novelle.is_author && user && user !== false && novelle.author?.is_creator && novelle.author?.username && (
            <div className="mt-6" />
          )}
        </header>

        {/* Teaser / login-gate */}
        {isGuest ? (
          <div className="editorial-card p-10 text-center" data-testid="novelle-guest-gate">
            <div className="h-14 w-14 rounded-full bg-[var(--magenta)]/15 mx-auto mb-5 flex items-center justify-center">
              <Lock className="h-6 w-6 text-[var(--magenta)]" />
            </div>
            <h3 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Logg inn for å lese</h3>
            <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-sm mx-auto mb-6 leading-relaxed">
              Noveller er kun tilgjengelig for innloggede medlemmer. Registrer deg gratis eller logg inn for å fortsette.
            </p>
            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="guest-login-cta">
              Logg inn
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <div className="font-meta mb-3">Sammendrag</div>
              <p className="font-fraunces italic text-xl text-[var(--cream)] leading-relaxed">
                {novelle.teaser}
              </p>
            </div>

            {/* Innhold eller paywall */}
            {canRead ? (
              <article className="font-fraunces text-lg text-[var(--cream)] leading-[1.9] whitespace-pre-wrap" data-testid="novelle-body">
                {novelle.body}
              </article>
            ) : (
              <div className="editorial-card p-10 text-center" data-testid="novelle-paywall">
                <div className="h-14 w-14 rounded-full bg-[var(--magenta)]/15 mx-auto mb-5 flex items-center justify-center">
                  <Lock className="h-6 w-6 text-[var(--magenta)]" />
                </div>
                <div className="font-meta mb-2">Resten er låst</div>
                <h3 className="font-fraunces italic text-3xl text-[var(--cream)] mb-4">
                  Lås opp for {novelle.token_cost} tokens
                </h3>
                <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-sm mx-auto mb-6 leading-relaxed">
                  90 % av summen går til forfatteren, 10 % til plattformen. Du betaler kun én gang
                  og kan lese novellen så mange ganger du vil.
                </p>
                <div className="font-meta mb-5">
                  Din balanse:{" "}
                  <span className={tokenBalance >= novelle.token_cost ? "text-[var(--cream)]" : "text-[var(--magenta)]"}>
                    {tokenBalance} tokens
                  </span>
                </div>
                <div className="inline-flex">
                  <BuyContentButton
                    contentType="novelle"
                    contentId={novelle.id}
                    price={novelle.token_cost}
                    hasAccess={false}
                    sellerUsername={novelle.author?.username}
                    onPurchased={() => load()}
                  />
                </div>
                {tokenBalance < novelle.token_cost && (
                  <div className="mt-5 font-meta text-[10px] text-[var(--cream-faint)] max-w-sm mx-auto">
                    Du har ikke nok tokens. <Link to="/kjop" className="underline hover:text-[var(--magenta)]">Kjøp tokens her</Link>.
                  </div>
                )}
                {error && <div className="mt-4 font-medium text-sm text-[var(--cream)] border-l-2 border-[var(--magenta)] pl-3">{error}</div>}
              </div>
            )}
          </>
        )}

        {/* Like + rating */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-wrap items-center justify-between gap-6">
          <button
            onClick={toggleLike}
            disabled={!user || user === false || novelle.is_author}
            className={`inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-[0.2em] transition-colors ${
              novelle.user_liked ? "text-[var(--magenta)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="novelle-like-btn"
          >
            <Heart className={`h-4 w-4 ${novelle.user_liked ? "fill-[var(--magenta)]" : ""}`} />
            {novelle.like_count} {novelle.like_count === 1 ? "like" : "likes"}
          </button>

          <div className="flex items-center gap-3" data-testid="novelle-rating">
            <span className="font-meta">Vurdering</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  onClick={() => rate(i)}
                  disabled={!user || user === false || (!canRead && !novelle.is_author)}
                  className="disabled:opacity-30"
                  data-testid={`novelle-rate-${i}`}
                >
                  <Star
                    className={`h-5 w-5 transition-colors ${
                      i <= (novelle.user_rating || 0)
                        ? "fill-[var(--magenta)] text-[var(--magenta)]"
                        : i <= Math.round(novelle.avg_rating)
                        ? "fill-white/30 text-white/30"
                        : "text-white/20 hover:text-[var(--magenta)]"
                    }`}
                  />
                </button>
              ))}
            </div>
            <span className="font-meta">
              {novelle.avg_rating > 0
                ? `${novelle.avg_rating.toFixed(1)} (${novelle.rating_count})`
                : "Ingen vurderinger"}
            </span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
